import type { ReactElement } from 'react';

/**
 * The viewer, seen from behind, sitting under the tree (v4.0 §4.17).
 *
 * One silhouette per animal in the avatar pool (src/shared/ember-avatars.js),
 * built from a small parametric vocabulary — head, ears, neck, body, and the
 * few things that read from the back (a mane, a bushy tail, a shell, a crest,
 * a long neck) — rather than 49 hand-drawn paths. The figure is a dark shape
 * with one warm rim of light along its top edges: the tree's lights falling
 * on the back of a head. No face, no expression; a creature someone is, not a
 * portrait of them.
 *
 * Geometry lives in a 200×200 box with the ground at y=176 and the figure
 * centred on x=100. Each spec is in those units.
 */

type EarKind = 'point' | 'round' | 'long' | 'floppy' | 'tuft' | 'wide' | 'none';

interface Ears {
  kind: EarKind;
  w: number;
  h: number;
  /** horizontal offset of each ear's base from the head centre */
  dx: number;
  /** vertical offset of the ear base from the head centre (negative = higher) */
  dy: number;
  /** outward tilt in degrees */
  tilt?: number;
}

type Extra =
  | 'mane' | 'crest' | 'ossicones' | 'antennae' | 'eyestalks' | 'spines'
  | 'tail-bushy' | 'tail-curl' | 'tail-fan' | 'tail-thick' | 'dorsal' | 'flukes'
  | 'shell' | 'wings' | 'topknot' | 'eye-bumps' | 'tentacles' | 'neck-mane';

interface Spec {
  head: { rx: number; ry: number };
  ears?: Ears;
  /** neck length above the shoulders (0 = head sits on the body) */
  neck?: { w: number; h: number; sway?: number };
  body: { w: number; h: number; kind?: 'shoulders' | 'dome' | 'upright' | 'slim' | 'arc' };
  extra?: Extra[];
}

const GROUND = 176;

const point = (w: number, h: number, dx: number, dy: number, tilt = 14): Ears => ({ kind: 'point', w, h, dx, dy, tilt });
const round = (w: number, h: number, dx: number, dy: number): Ears => ({ kind: 'round', w, h, dx, dy });
const long = (w: number, h: number, dx: number, dy: number, tilt = 8): Ears => ({ kind: 'long', w, h, dx, dy, tilt });
const floppy = (w: number, h: number, dx: number, dy: number): Ears => ({ kind: 'floppy', w, h, dx, dy });
const wide = (w: number, h: number, dx: number, dy: number, tilt = 30): Ears => ({ kind: 'wide', w, h, dx, dy, tilt });
const tuft = (w: number, h: number, dx: number, dy: number): Ears => ({ kind: 'tuft', w, h, dx, dy, tilt: 18 });

export const ANIMAL_BACKS: Record<string, Spec> = {
  // woodland
  'red-fox': { head: { rx: 26, ry: 23 }, ears: point(17, 30, 16, -14), neck: { w: 30, h: 8 }, body: { w: 74, h: 60 }, extra: ['tail-bushy'] },
  'arctic-fox': { head: { rx: 27, ry: 24 }, ears: point(15, 24, 15, -14), neck: { w: 34, h: 6 }, body: { w: 82, h: 60 }, extra: ['tail-bushy'] },
  rabbit: { head: { rx: 22, ry: 22 }, ears: long(13, 54, 9, -16, 6), body: { w: 60, h: 66, kind: 'upright' } },
  hedgehog: { head: { rx: 17, ry: 14 }, ears: round(7, 7, 12, -8), body: { w: 84, h: 52, kind: 'dome' }, extra: ['spines'] },
  squirrel: { head: { rx: 20, ry: 19 }, ears: tuft(9, 16, 14, -12), body: { w: 54, h: 58, kind: 'upright' }, extra: ['tail-fan'] },
  chipmunk: { head: { rx: 18, ry: 17 }, ears: round(8, 9, 13, -9), body: { w: 50, h: 50, kind: 'upright' }, extra: ['tail-curl'] },
  fawn: { head: { rx: 19, ry: 21 }, ears: wide(14, 26, 14, -10, 38), neck: { w: 22, h: 36 }, body: { w: 62, h: 52 } },
  raccoon: { head: { rx: 26, ry: 22 }, ears: point(12, 16, 17, -12, 20), body: { w: 78, h: 60 }, extra: ['tail-curl'] },
  badger: { head: { rx: 26, ry: 20 }, ears: round(8, 8, 18, -9), body: { w: 90, h: 54 } },
  beaver: { head: { rx: 24, ry: 20 }, ears: round(7, 7, 17, -9), body: { w: 92, h: 58, kind: 'dome' } },
  // wild & far away
  'brown-bear': { head: { rx: 31, ry: 27 }, ears: round(14, 14, 22, -14), body: { w: 104, h: 70 } },
  'polar-bear': { head: { rx: 28, ry: 24 }, ears: round(10, 10, 20, -12), neck: { w: 44, h: 10 }, body: { w: 108, h: 70 } },
  panda: { head: { rx: 33, ry: 28 }, ears: round(18, 18, 24, -16), body: { w: 98, h: 68 } },
  'red-panda': { head: { rx: 28, ry: 24 }, ears: point(18, 18, 20, -12, 24), body: { w: 76, h: 58 }, extra: ['tail-bushy'] },
  koala: { head: { rx: 31, ry: 27 }, ears: round(26, 24, 26, -8), body: { w: 84, h: 66 } },
  kangaroo: { head: { rx: 17, ry: 20 }, ears: long(13, 34, 10, -14, 22), neck: { w: 22, h: 22 }, body: { w: 66, h: 66, kind: 'upright' }, extra: ['tail-thick'] },
  sloth: { head: { rx: 25, ry: 23 }, body: { w: 70, h: 72, kind: 'upright' } },
  elephant: { head: { rx: 31, ry: 28 }, ears: wide(46, 44, 22, -4, 14), body: { w: 108, h: 74 } },
  giraffe: { head: { rx: 15, ry: 18 }, ears: wide(10, 16, 11, -10, 40), neck: { w: 20, h: 74 }, body: { w: 84, h: 52 }, extra: ['ossicones'] },
  zebra: { head: { rx: 16, ry: 22 }, ears: point(11, 22, 10, -16, 10), neck: { w: 26, h: 42 }, body: { w: 76, h: 56 }, extra: ['neck-mane'] },
  tiger: { head: { rx: 29, ry: 24 }, ears: round(14, 13, 21, -13), body: { w: 94, h: 64 } },
  lion: { head: { rx: 26, ry: 24 }, ears: round(11, 11, 19, -12), body: { w: 94, h: 64 }, extra: ['mane'] },
  wolf: { head: { rx: 26, ry: 24 }, ears: point(16, 30, 15, -15), neck: { w: 32, h: 10 }, body: { w: 84, h: 62 } },
  // field & farm
  horse: { head: { rx: 16, ry: 20 }, ears: point(10, 20, 9, -15, 8), neck: { w: 28, h: 54 }, body: { w: 86, h: 58 }, extra: ['neck-mane'] },
  sheep: { head: { rx: 22, ry: 20 }, ears: floppy(12, 22, 20, -4), body: { w: 94, h: 62, kind: 'dome' } },
  alpaca: { head: { rx: 18, ry: 20 }, ears: point(10, 24, 10, -14, 10), neck: { w: 22, h: 66 }, body: { w: 82, h: 58 }, extra: ['topknot'] },
  capybara: { head: { rx: 27, ry: 22 }, ears: round(7, 7, 19, -12), body: { w: 100, h: 56 } },
  // riverside & home
  otter: { head: { rx: 22, ry: 18 }, ears: round(6, 6, 16, -8), neck: { w: 26, h: 12 }, body: { w: 58, h: 62, kind: 'slim' } },
  seal: { head: { rx: 22, ry: 20 }, body: { w: 86, h: 62, kind: 'dome' } },
  cat: { head: { rx: 24, ry: 22 }, ears: point(16, 22, 14, -14, 12), body: { w: 58, h: 60, kind: 'upright' }, extra: ['tail-curl'] },
  dog: { head: { rx: 26, ry: 24 }, ears: floppy(16, 32, 22, -10), body: { w: 80, h: 62 } },
  hamster: { head: { rx: 28, ry: 24 }, ears: round(10, 10, 20, -14), body: { w: 84, h: 58, kind: 'dome' } },
  mouse: { head: { rx: 20, ry: 18 }, ears: round(22, 22, 18, -10), body: { w: 54, h: 48, kind: 'dome' } },
  // birds
  owl: { head: { rx: 31, ry: 26 }, ears: tuft(10, 18, 22, -18), body: { w: 72, h: 66, kind: 'upright' } },
  penguin: { head: { rx: 20, ry: 20 }, body: { w: 64, h: 82, kind: 'upright' } },
  duckling: { head: { rx: 20, ry: 20 }, body: { w: 66, h: 52, kind: 'dome' } },
  robin: { head: { rx: 16, ry: 16 }, body: { w: 50, h: 44, kind: 'dome' }, extra: ['tail-fan'] },
  'blue-jay': { head: { rx: 16, ry: 16 }, body: { w: 50, h: 44, kind: 'dome' }, extra: ['crest'] },
  flamingo: { head: { rx: 10, ry: 10 }, neck: { w: 9, h: 92, sway: 18 }, body: { w: 72, h: 46, kind: 'dome' } },
  hummingbird: { head: { rx: 12, ry: 12 }, body: { w: 38, h: 40, kind: 'dome' }, extra: ['wings'] },
  puffin: { head: { rx: 20, ry: 20 }, body: { w: 62, h: 66, kind: 'upright' } },
  // sea
  dolphin: { head: { rx: 0, ry: 0 }, body: { w: 96, h: 60, kind: 'arc' }, extra: ['dorsal'] },
  whale: { head: { rx: 0, ry: 0 }, body: { w: 116, h: 64, kind: 'arc' }, extra: ['dorsal', 'flukes'] },
  'sea-turtle': { head: { rx: 14, ry: 13 }, body: { w: 100, h: 56, kind: 'dome' }, extra: ['shell'] },
  koi: { head: { rx: 0, ry: 0 }, body: { w: 40, h: 78, kind: 'slim' }, extra: ['flukes'] },
  octopus: { head: { rx: 34, ry: 32 }, body: { w: 70, h: 44, kind: 'dome' }, extra: ['tentacles'] },
  // small wonders
  frog: { head: { rx: 28, ry: 17 }, body: { w: 74, h: 42, kind: 'dome' }, extra: ['eye-bumps'] },
  butterfly: { head: { rx: 7, ry: 7 }, body: { w: 12, h: 56, kind: 'slim' }, extra: ['wings', 'antennae'] },
  bee: { head: { rx: 12, ry: 11 }, body: { w: 56, h: 44, kind: 'dome' }, extra: ['wings', 'antennae'] },
  snail: { head: { rx: 9, ry: 8 }, body: { w: 72, h: 60, kind: 'dome' }, extra: ['shell', 'eyestalks'] },
};

export const DEFAULT_ANIMAL = 'red-fox';

function ellipse(cx: number, cy: number, rx: number, ry: number): string {
  return `M${cx - rx} ${cy} a${rx} ${ry} 0 1 0 ${rx * 2} 0 a${rx} ${ry} 0 1 0 ${-rx * 2} 0Z`;
}

/** A rounded-shoulder body standing on the ground: a flattened dome or a taller egg. */
function bodyPath(spec: Spec): { d: string; top: number } {
  const { w, h, kind = 'shoulders' } = spec.body;
  const half = w / 2;
  const top = GROUND - h;
  if (kind === 'dome' || kind === 'shoulders') {
    // wide at the ground, rounded over the shoulders
    const shoulderR = kind === 'dome' ? h : Math.min(h, half * 0.9);
    return {
      top,
      d: `M${100 - half} ${GROUND} L${100 - half} ${top + shoulderR} Q${100 - half} ${top} ${100 - half + shoulderR} ${top} L${100 + half - shoulderR} ${top} Q${100 + half} ${top} ${100 + half} ${top + shoulderR} L${100 + half} ${GROUND}Z`,
    };
  }
  if (kind === 'upright' || kind === 'slim') {
    // an egg standing up
    return { top, d: `M${100 - half} ${GROUND} C${100 - half} ${top + h * 0.35} ${100 - half * 0.7} ${top} 100 ${top} C${100 + half * 0.7} ${top} ${100 + half} ${top + h * 0.35} ${100 + half} ${GROUND}Z` };
  }
  // arc: a back rising out of the ground (sea animals)
  return { top, d: `M${100 - half} ${GROUND} C${100 - half * 0.9} ${top + 4} ${100 - half * 0.3} ${top} 100 ${top} C${100 + half * 0.3} ${top} ${100 + half * 0.9} ${top + 4} ${100 + half} ${GROUND}Z` };
}

function earPath(e: Ears, cx: number, cy: number, side: -1 | 1): string {
  const bx = cx + side * e.dx;
  const by = cy + e.dy;
  const tilt = (e.tilt ?? 0) * side;
  const rot = (x: number, y: number): [number, number] => {
    const r = (tilt * Math.PI) / 180;
    const dx = x - bx;
    const dy = y - by;
    return [bx + dx * Math.cos(r) - dy * Math.sin(r), by + dx * Math.sin(r) + dy * Math.cos(r)];
  };
  const p = (x: number, y: number): string => rot(x, y).map((v) => v.toFixed(1)).join(' ');
  const hw = e.w / 2;
  switch (e.kind) {
    case 'point':
    case 'tuft':
      return `M${p(bx - hw, by + 4)} Q${p(bx - hw * 0.4, by - e.h * 0.55)} ${p(bx, by - e.h)} Q${p(bx + hw * 0.4, by - e.h * 0.55)} ${p(bx + hw, by + 4)}Z`;
    case 'round':
      return `M${p(bx - hw, by + 2)} A${hw} ${e.h / 2} 0 1 1 ${p(bx + hw, by + 2)}Z`;
    case 'long':
      return `M${p(bx - hw, by + 6)} C${p(bx - hw, by - e.h * 0.7)} ${p(bx - hw * 0.5, by - e.h)} ${p(bx, by - e.h)} C${p(bx + hw * 0.5, by - e.h)} ${p(bx + hw, by - e.h * 0.7)} ${p(bx + hw, by + 6)}Z`;
    case 'wide':
      // a broad leaf ear standing out from the head
      return `M${p(bx - hw * 0.4, by + 6)} C${p(bx - hw * 0.3, by - e.h * 0.6)} ${p(bx + hw * 0.6, by - e.h)} ${p(bx + hw, by - e.h * 0.5)} C${p(bx + hw * 1.1, by)} ${p(bx + hw * 0.6, by + e.h * 0.3)} ${p(bx, by + e.h * 0.35)}Z`;
    case 'floppy':
      return `M${p(bx - hw, by - 4)} C${p(bx + hw * 0.6, by - 6)} ${p(bx + hw * 1.1, by + e.h * 0.5)} ${p(bx + hw * 0.6, by + e.h)} C${p(bx, by + e.h * 0.9)} ${p(bx - hw, by + e.h * 0.4)} ${p(bx - hw, by - 4)}Z`;
    default:
      return '';
  }
}

function extrasPaths(spec: Spec, headCx: number, headCy: number, bodyTop: number): string[] {
  const out: string[] = [];
  const { rx, ry } = spec.head;
  const half = spec.body.w / 2;
  for (const x of spec.extra || []) {
    switch (x) {
      case 'mane':
        out.push(ellipse(headCx, headCy + 8, rx * 1.75, ry * 1.6));
        break;
      case 'neck-mane':
        if (spec.neck) out.push(`M${headCx - 2} ${headCy + ry - 6} L${headCx + 6} ${headCy + ry - 4} L${headCx + 9} ${bodyTop + 4} L${headCx - 5} ${bodyTop + 2}Z`);
        break;
      case 'crest':
        out.push(`M${headCx - 4} ${headCy - ry + 4} Q${headCx + 4} ${headCy - ry - 22} ${headCx + 14} ${headCy - ry - 10} Q${headCx + 6} ${headCy - ry - 6} ${headCx + 8} ${headCy - ry + 4}Z`);
        break;
      case 'topknot':
        out.push(ellipse(headCx, headCy - ry + 2, rx * 0.9, 9));
        break;
      case 'ossicones':
        for (const s of [-1, 1]) out.push(`M${headCx + s * 6 - 2.5} ${headCy - ry + 2} L${headCx + s * 7 - 2} ${headCy - ry - 14} A3 3 0 1 1 ${headCx + s * 7 + 3} ${headCy - ry - 13} L${headCx + s * 6 + 2.5} ${headCy - ry + 2}Z`);
        break;
      case 'antennae':
        for (const s of [-1, 1]) out.push(`M${headCx + s * 2 - 1} ${headCy - ry + 2} L${headCx + s * 9 - 1} ${headCy - ry - 18} A2.4 2.4 0 1 1 ${headCx + s * 9 + 2.5} ${headCy - ry - 17} L${headCx + s * 2 + 1} ${headCy - ry + 2}Z`);
        break;
      case 'eyestalks':
        for (const s of [-1, 1]) out.push(`M${headCx + s * 3 - 1.5} ${headCy} L${headCx + s * 8 - 1.5} ${headCy - 22} A3 3 0 1 1 ${headCx + s * 8 + 3} ${headCy - 21} L${headCx + s * 3 + 1.5} ${headCy}Z`);
        break;
      case 'eye-bumps':
        for (const s of [-1, 1]) out.push(ellipse(headCx + s * rx * 0.62, headCy - ry * 0.75, 8, 7));
        break;
      case 'spines': {
        let d = '';
        for (let i = 0; i < 11; i += 1) {
          const t = (i / 10) * Math.PI;
          const bx = 100 - Math.cos(t) * half * 0.9;
          const by = bodyTop + (1 - Math.sin(t)) * 18 + 4;
          const tipX = 100 - Math.cos(t) * (half * 0.9 + 12);
          const tipY = by - Math.sin(t) * 13 - 4;
          d += `M${bx - 4} ${by + 3} L${tipX} ${tipY} L${bx + 4} ${by + 3}Z`;
        }
        out.push(d);
        break;
      }
      case 'tail-bushy':
        out.push(`M${100 + half - 6} ${GROUND} C${100 + half + 8} ${GROUND - 20} ${100 + half + 30} ${GROUND - 24} ${100 + half + 26} ${GROUND - 50} C${100 + half + 36} ${GROUND - 34} ${100 + half + 34} ${GROUND - 8} ${100 + half + 18} ${GROUND}Z`);
        break;
      case 'tail-curl':
        out.push(`M${100 + half - 4} ${GROUND} C${100 + half + 6} ${GROUND - 12} ${100 + half + 18} ${GROUND - 24} ${100 + half + 10} ${GROUND - 40} C${100 + half + 22} ${GROUND - 30} ${100 + half + 22} ${GROUND - 6} ${100 + half + 8} ${GROUND}Z`);
        break;
      case 'tail-thick':
        out.push(`M${100 - half + 4} ${GROUND} C${100 - half - 10} ${GROUND - 6} ${100 - half - 36} ${GROUND - 2} ${100 - half - 44} ${GROUND} L${100 - half - 44} ${GROUND + 1} L${100 - half + 4} ${GROUND + 1}Z`);
        break;
      case 'tail-fan':
        // the big tail rising beside the head, the squirrel's signature from behind
        out.push(`M${100 + half - 8} ${GROUND} C${100 + half + 4} ${GROUND - 40} ${100 + half + 2} ${headCy - 10} ${headCx + rx + 6} ${headCy - ry - 18} C${headCx + rx + 26} ${headCy - ry - 4} ${100 + half + 30} ${GROUND - 60} ${100 + half + 22} ${GROUND}Z`);
        break;
      case 'dorsal':
        out.push(`M${100 - 8} ${bodyTop + 2} C${100 - 4} ${bodyTop - 14} ${100 + 8} ${bodyTop - 26} ${100 + 16} ${bodyTop - 26} C${100 + 12} ${bodyTop - 14} ${100 + 12} ${bodyTop - 4} ${100 + 12} ${bodyTop + 2}Z`);
        break;
      case 'flukes':
        if (spec.body.kind === 'slim') {
          out.push(`M${100 - 6} ${bodyTop + 6} C${100 - 26} ${bodyTop - 10} ${100 - 30} ${bodyTop - 30} ${100 - 20} ${bodyTop - 38} C${100 - 10} ${bodyTop - 22} ${100 - 4} ${bodyTop - 8} 100 ${bodyTop - 4} C${100 + 4} ${bodyTop - 8} ${100 + 10} ${bodyTop - 22} ${100 + 20} ${bodyTop - 38} C${100 + 30} ${bodyTop - 30} ${100 + 26} ${bodyTop - 10} ${100 + 6} ${bodyTop + 6}Z`);
        } else {
          out.push(`M${100 + half - 10} ${GROUND} C${100 + half + 6} ${GROUND - 12} ${100 + half + 26} ${GROUND - 30} ${100 + half + 40} ${GROUND - 30} C${100 + half + 30} ${GROUND - 16} ${100 + half + 30} ${GROUND - 6} ${100 + half + 34} ${GROUND}Z`);
        }
        break;
      case 'shell':
        out.push(ellipse(100, bodyTop + spec.body.h * 0.55, half * 0.92, spec.body.h * 0.52));
        break;
      case 'wings':
        if (spec.body.kind === 'slim') {
          // butterfly: two pairs, spread
          out.push(`M100 ${bodyTop + 10} C${100 - 30} ${bodyTop - 24} ${100 - 66} ${bodyTop - 10} ${100 - 60} ${bodyTop + 22} C${100 - 56} ${bodyTop + 38} ${100 - 30} ${bodyTop + 40} 100 ${bodyTop + 30}Z`);
          out.push(`M100 ${bodyTop + 10} C${100 + 30} ${bodyTop - 24} ${100 + 66} ${bodyTop - 10} ${100 + 60} ${bodyTop + 22} C${100 + 56} ${bodyTop + 38} ${100 + 30} ${bodyTop + 40} 100 ${bodyTop + 30}Z`);
          out.push(`M100 ${bodyTop + 30} C${100 - 20} ${bodyTop + 30} ${100 - 46} ${bodyTop + 48} ${100 - 36} ${bodyTop + 66} C${100 - 22} ${bodyTop + 74} ${100 - 6} ${bodyTop + 60} 100 ${bodyTop + 50}Z`);
          out.push(`M100 ${bodyTop + 30} C${100 + 20} ${bodyTop + 30} ${100 + 46} ${bodyTop + 48} ${100 + 36} ${bodyTop + 66} C${100 + 22} ${bodyTop + 74} ${100 + 6} ${bodyTop + 60} 100 ${bodyTop + 50}Z`);
        } else {
          for (const s of [-1, 1]) out.push(`M${100 + s * 6} ${bodyTop + 6} C${100 + s * 20} ${bodyTop - 14} ${100 + s * 44} ${bodyTop - 16} ${100 + s * 48} ${bodyTop - 2} C${100 + s * 40} ${bodyTop + 8} ${100 + s * 20} ${bodyTop + 12} ${100 + s * 6} ${bodyTop + 10}Z`);
        }
        break;
      case 'tentacles':
        for (let i = 0; i < 6; i += 1) {
          const x = 100 - 30 + i * 12;
          out.push(`M${x - 4} ${bodyTop + 6} C${x - 8} ${bodyTop + 30} ${x + 10} ${bodyTop + 44} ${x + 2} ${GROUND} L${x + 8} ${GROUND} C${x + 16} ${bodyTop + 40} ${x} ${bodyTop + 28} ${x + 4} ${bodyTop + 6}Z`);
        }
        break;
      default:
        break;
    }
  }
  return out;
}

function headCentre(spec: Spec, bodyTop: number): { cx: number; cy: number } {
  const neckH = spec.neck?.h ?? 0;
  const sway = spec.neck?.sway ?? 0;
  return {
    cx: 100 + sway,
    cy: bodyTop - neckH - spec.head.ry + (spec.neck ? 6 : Math.min(10, spec.head.ry * 0.4)),
  };
}

/** Where this creature's mouth is, seen from behind: the side of its head, in
 *  the 200-unit box — `dx` right of the figure's centre, `up` above its ground
 *  line. Where something it says comes from. A headless back (a whale's arc)
 *  speaks from the top of the body. */
export function animalMouth(animal: string): { dx: number; up: number } {
  const spec = ANIMAL_BACKS[animal] || ANIMAL_BACKS[DEFAULT_ANIMAL];
  const { cx, cy } = headCentre(spec, bodyPath(spec).top);
  return { dx: cx - 100 + spec.head.rx, up: GROUND - cy };
}

/** The top of this creature's head (ears and horns aside), in the same units:
 *  where a comic mark over its head — a `!`, a `?`, a note — stands. */
export function animalCrown(animal: string): { dx: number; up: number } {
  const spec = ANIMAL_BACKS[animal] || ANIMAL_BACKS[DEFAULT_ANIMAL];
  const { cx, cy } = headCentre(spec, bodyPath(spec).top);
  return { dx: cx - 100, up: GROUND - cy + spec.head.ry };
}

/** Every path of the silhouette, in paint order (back to front), in two
 *  groups: the body (with whatever stands behind it or grows from it — tails,
 *  wings, a shell, a neck) and the head (ears, the head itself, and what sits
 *  on it — horns, a crest, eye bumps). The head is its own group so it can
 *  move on its own: it wags while the creature talks (§4.17). */
export function animalBackPaths(animal: string): { body: string[]; head: string[] } {
  const spec = ANIMAL_BACKS[animal] || ANIMAL_BACKS[DEFAULT_ANIMAL];
  const body = bodyPath(spec);
  const neckH = spec.neck?.h ?? 0;
  const { cx: headCx, cy: headCy } = headCentre(spec, body.top);
  const paths: string[] = [];
  // Extras split by paint order: tails, wings, manes, shells behind the body;
  // horns, crests, eye bumps in front of the head. A neck mane runs from the
  // head down the neck, so it stays with the body (drawn just under the head).
  const behind = new Set<Extra>(['tail-bushy', 'tail-curl', 'tail-fan', 'tail-thick', 'flukes', 'wings', 'mane', 'shell', 'spines', 'tentacles', 'neck-mane']);
  const front: string[] = [];
  for (const x of spec.extra || []) {
    const drawn = extrasPaths({ ...spec, extra: [x] }, headCx, headCy, body.top);
    (behind.has(x) ? paths : front).push(...drawn);
  }
  if (spec.neck) {
    const nw = spec.neck.w / 2;
    paths.push(`M${100 - nw} ${body.top + 6} C${100 - nw} ${body.top - neckH * 0.5} ${headCx - nw} ${headCy + spec.head.ry * 0.2} ${headCx - nw * 0.9} ${headCy} L${headCx + nw * 0.9} ${headCy} C${headCx + nw} ${headCy + spec.head.ry * 0.2} ${100 + nw} ${body.top - neckH * 0.5} ${100 + nw} ${body.top + 6}Z`);
  }
  paths.push(body.d);
  const head: string[] = [];
  if (spec.ears && spec.ears.kind !== 'none') {
    head.push(earPath(spec.ears, headCx, headCy, -1), earPath(spec.ears, headCx, headCy, 1));
  }
  if (spec.head.rx > 0) head.push(ellipse(headCx, headCy, spec.head.rx, spec.head.ry));
  head.push(...front);
  return { body: paths.filter(Boolean), head: head.filter(Boolean) };
}

/**
 * The figure as an SVG group, to be placed by the scene (the scene owns the
 * viewBox). `size` scales the 200-unit box; the ground line of the figure sits
 * at (x, y).
 */
export function AnimalBack({ animal, x, y, size, rimId, speaking = false, reaction = null }: {
  animal: string;
  x: number;
  y: number;
  size: number;
  /** id of the warm rim gradient defined by the scene */
  rimId: string;
  /** the creature is talking: its head wags (§4.17 ceremony) */
  speaking?: boolean;
  /** someone poked it: the id of the reaction it gives (a `.is-poke-<id>`
   *  class; the scene's CSS says what the head, and sometimes the whole
   *  figure, does) */
  reaction?: string | null;
}): ReactElement {
  const { body, head } = animalBackPaths(animal);
  const k = size / 200;
  const transform = `translate(${x - 100 * k} ${y - GROUND * k}) scale(${k})`;
  const layer = (fill: string, dy: number, key: string): ReactElement => (
    <g fill={fill} transform={`translate(0 ${dy})`}>
      {body.map((d, i) => <path key={`${key}${i}`} d={d} />)}
      <g className="banyan-you-head">{head.map((d, i) => <path key={`${key}h${i}`} d={d} />)}</g>
    </g>
  );
  return (
    <g className={`banyan-you${speaking ? ' is-speaking' : ''}${reaction ? ` is-poke is-poke-${reaction}` : ''}`} aria-hidden>
      {/* the seat's shadow: the scene ends at the ground line, so only its upper half shows, behind the figure's base */}
      <ellipse cx={x} cy={y} rx={size * 0.42} ry={size * 0.05} fill="#000" opacity=".35" />
      <g transform={transform}>
        {/* the whole figure's own movement (a hop, a wiggle) goes on this
            group, which carries no transform attribute of its own — a CSS
            transform animation replaces an element's attribute transform */}
        <g className="banyan-you-figure">
          {layer(`url(#${rimId})`, 0, 'r')}
          {layer('var(--banyan-you)', 2.6, 'd')}
        </g>
      </g>
    </g>
  );
}
